const API_URL = process.env.REACT_APP_API_URL;

const cleanAuthToken = () => {
  const rawToken = localStorage.getItem('auth_token');
  if (!rawToken || typeof rawToken !== 'string') {
    return null;
  }
  const token = rawToken.replace(/\r?\n/g, '').trim();
  if (!token || /[\u0000-\u001F\u007F]/.test(token)) {
    return null;
  }
  return token;
};

const authHeader = () => {
  const token = cleanAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : null;
};

const authHeaders = (additionalHeaders = {}) => ({
  'Content-Type': 'application/json',
  ...(authHeader() || {}),
  ...additionalHeaders
});

export const checkGroqKey = async (key) => {
  try {
    const response = await fetch(`${API_URL}/recruitment/resume/check-groq-key`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ key })
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      return { success: false, message: error.message || 'Unable to validate Groq key' };
    }

    const data = await response.json();
    return { success: data.success, message: data.message };
  } catch (err) {
    console.error('checkGroqKey API Error:', err);
    return { success: false, message: 'Network error' };
  }
};

export const extractResumeSection = async ({ file, section, key, jobDescription }) => {
  try {
    const formData = new FormData();
    formData.append('resumeFile', file);
    formData.append('section', section);
    if (key) formData.append('key', key);

    if (jobDescription) {
      formData.append('jobDescription', jobDescription);
    }

    console.log('API: extractResumeSection -> sending request', { section, fileName: file?.name, fileSize: file?.size, hasKey: !!key });
    const response = await fetch(`${API_URL}/recruitment/resume/extract-section`, {
      method: 'POST',
      body: formData,
    });

    console.log('API: extractResumeSection -> response status', response.status);
    const parsed = await response.json().catch(() => null);
    console.log('API: extractResumeSection -> response body', parsed);
    if (!response.ok) {
      return {
        success: false,
        message: parsed?.message || `HTTP Error: ${response.status}`,
        details: parsed?.details || null,
      };
    }

    return { success: true, data: parsed?.data || parsed };
  } catch (err) {
    console.error('extractResumeSection API Error:', err);
    return { success: false, message: 'Network error: ' + (err?.message || String(err)) };
  }
};

// Scrapper API
export const getScrapperData = async (filters) => {
  try {
    const rawFilters = filters || {};
    const payload = Object.entries(rawFilters).reduce((acc, [key, value]) => {
      if (value === undefined || value === null) return acc;
      if (typeof value === 'string' && value.trim() === '') return acc;
      acc[key] = typeof value === 'string' ? value.trim() : value;
      return acc;
    }, {});

    const url = `${API_URL}/scrapper/scrapper/data`;

    // console.log('📡 UI calling scrapper API:', url, payload);

    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });

    const responseData = await response.json().catch(() => ({}));
    // console.log('📡 Scrapper API response:', response.status, responseData);

    if (!response.ok) {
      throw new Error(responseData.message || `HTTP Error: ${response.status}`);
    }

    return responseData;
  } catch (err) {
    console.error('API Error:', err);
    throw err;
  }
};

// Login API
export const loginEmployee = async ({ username, password }) => {
  try {
    const response = await fetch(`${API_URL}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password })
    });
    if (!response.ok) {
      const error = await response.json();
      return { success: false, message: error.message };
    }
    const data = await response.json();
    return { success: true, data };
  } catch (err) {
    return { success: false, message: 'Network error' };
  }
};

// ============================================================
// ROLE-BASED LOGIN APIs
// ============================================================

// Role-Based Login (RECRUITER, HR, ADMIN)
export const roleBasedLogin = async ({ email, password }) => {
  try {
    const response = await fetch(`${API_URL}/recruitment-auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    });
    console.log('📥 API: Login request completed with status:', response.status);

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Unknown error' }));
      console.error('❌ API: Login failed');
      return { success: false, message: error.message || 'Login failed' };
    }
    const data = await response.json();
    console.log('✅ API: Login successful response received');
    return { success: true, data };
  } catch (err) {
    console.error('💥 API roleBasedLogin error');
    return { success: false, message: 'Network error' };
  }
};

// Role-Based Register
export const roleBasedRegister = async ({ username, email, password, first_name, last_name, role = 'RECRUITER' }) => {
  try {
    const response = await fetch(`${API_URL}/recruitment-auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, email, password, first_name, last_name, role })
    });
    if (!response.ok) {
      const error = await response.json();
      return { success: false, message: error.message };
    }
    const data = await response.json();
    return { success: true, data };
  } catch (err) {
    return { success: false, message: 'Network error' };
  }
};

export const getUserRoles = async () => {
  try {
    const token = cleanAuthToken();
    if (!token) {
      return { success: false, message: 'Authentication token is missing or invalid' };
    }
    const response = await fetch(`${API_URL}/recruitment-auth/roles`, {
      method: 'GET',
      headers: authHeaders()
    });

    if (!response.ok) {
      const error = await response.json();
      return { success: false, message: error.message };
    }

    const data = await response.json();
    return { success: true, data: data.roles || [] };
  } catch (err) {
    return { success: false, message: 'Network error' };
  }
};

export const createUser = async ({ username, email, password, first_name, last_name, role }) => {
  try {
    const token = cleanAuthToken();
    if (!token) {
      console.error('⚠️ [API] No valid token found');
      return { success: false, message: 'Authentication token is missing or invalid' };
    }

    const response = await fetch(`${API_URL}/recruitment-auth/create-user`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ username, email, password, first_name, last_name, role })
    });

    console.log('📡 [API] User creation request finished with status:', response.status);

    if (!response.ok) {
      let errorData;
      try {
        errorData = await response.json();
        console.error('❌ [API] Server error response received');
      } catch (e) {
        console.error('❌ [API] Could not parse error response as JSON');
        errorData = { message: 'Server error: ' + response.statusText };
      }
      return { success: false, message: errorData.message || 'Failed to create user' };
    }

    const data = await response.json();
    console.log('✅ [API] User created successfully');
    return { success: true, data };
  } catch (err) {
    console.error('💥 [API] createUser error');
    return { success: false, message: 'Network error: ' + err.message };
  }
};

// Verify Token
export const verifyToken = async (token) => {
  try {
    const cleanToken = token ? token.replace(/\r?\n/g, '').trim() : null;
    if (!cleanToken || /[\u0000-\u001F\u007F]/.test(cleanToken)) {
      return { success: false };
    }
    const response = await fetch(`${API_URL}/recruitment-auth/verify-token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${cleanToken}`
      }
    });
    if (!response.ok) {
      return { success: false };
    }
    const data = await response.json();
    return { success: true, user: data.user };
  } catch (err) {
    return { success: false };
  }
};

// Get Recruiter Candidates
export const getRecruiterCandidates = async (userId, token) => {
  try {
    const cleanToken = token ? token.replace(/\r?\n/g, '').trim() : null;
    if (!cleanToken) {
      return { success: false, message: 'Authentication token is missing or invalid' };
    }
    const response = await fetch(`${API_URL}/recruitment-auth/recruiter/${userId}/candidates`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${cleanToken}`
      }
    });
    if (!response.ok) {
      const error = await response.json();
      return { success: false, message: error.message };
    }
    const data = await response.json();
    return { success: true, candidates: data.candidates };
  } catch (err) {
    return { success: false, message: 'Network error' };
  }
};

// Get All Candidates (HR endpoint)
export const getAllCandidatesHR = async (token) => {
  try {
    const cleanToken = token ? token.replace(/\r?\n/g, '').trim() : null;
    if (!cleanToken) {
      return { success: false, message: 'Authentication token is missing or invalid' };
    }
    const response = await fetch(`${API_URL}/recruitment-auth/hr/candidates`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${cleanToken}`
      }
    });
    if (!response.ok) {
      const error = await response.json();
      return { success: false, message: error.message };
    }
    const data = await response.json();
    return { success: true, candidates: data.candidates };
  } catch (err) {
    return { success: false, message: 'Network error' };
  }
};

// Forgot Password API
export const forgotPassword = async ({ email }) => {
  try {
    const response = await fetch(`${API_URL}/auth/forgot-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email })
    });
    if (!response.ok) {
      const error = await response.json();
      return { success: false, message: error.message };
    }
    const data = await response.json();
    return { success: true, data };
  } catch (err) {
    return { success: false, message: 'Network error' };
  }
};

// Candidate Registration API
export const registerCandidate = async (formData) => {
  console.log("🔄 Starting candidate registration API call");
  try {
    console.log("📤 Sending POST request to /recruitment/candidate");
    const response = await fetch(`${API_URL}/recruitment/candidate`, {
      method: 'POST',
      body: formData
    });
    console.log("📥 Response status:", response.status);
    if (!response.ok) {
      const error = await response.json();
      console.log("❌ API error response:", error);
      return { success: false, message: error.message };
    }
    const data = await response.json();
    console.log("✅ API success response:", data);
    return { success: data.success, data: data, message: data.message };
  } catch (err) {
    console.log("💥 Network error in registerCandidate:", err);
    return { success: false, message: 'Network error: ' + err.message };
  }
};

// Update Candidate API
export const updateCandidate = async (candidateId, formData) => {
  console.log("🔄 Starting candidate update API call");
  try {
    console.log("📤 Sending PUT request to /recruitment/candidate/" + candidateId);
    console.log("📦 FormData contents:");
    for (let [key, value] of formData.entries()) {
      console.log(`   ${key}: ${value}`);
    }
    const token = cleanAuthToken();
    if (!token) {
      return { success: false, message: 'No authentication token. Please login again.' };
    }

    const response = await fetch(`${API_URL}/recruitment/candidate/${candidateId}`, {
      method: 'PUT',
      headers: authHeader() || {},
      body: formData
    });
    console.log("📥 Response status:", response.status);
    if (!response.ok) {
      const error = await response.json();
      console.log("❌ API error response:", error);
      return { success: false, message: error.message };
    }
    const data = await response.json();
    console.log("✅ API success response:", data);
    return { success: data.success, data: data, message: data.message };
  } catch (err) {
    console.log("💥 Network error in updateCandidate:", err);
    return { success: false, message: 'Network error: ' + err.message };
  }
};

// Get Candidates List API
export const getCandidates = async () => {
  try {
    const token = cleanAuthToken();

    if (!token) {
      console.error('❌ No authentication token found in localStorage or token invalid');
      return { success: false, message: 'No authentication token. Please login again.' };
    }

    const response = await fetch(`${API_URL}/recruitment/candidates`, {
      method: 'GET',
      headers: authHeaders()
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Unknown error' }));
      console.error('❌ API Error Response received');
      return { success: false, message: error.message || 'Failed to fetch candidates' };
    }

    const data = await response.json();
    console.log('✅ Candidates fetched successfully');
    return { success: data.success, data: data.data };
  } catch (err) {
    console.error('💥 Network error in getCandidates');
    return { success: false, message: 'Network error: ' + err.message };
  }
};

// Get Candidate Feedback API
export const getCandidateFeedback = async (candidateId) => {
  try {
    const token = cleanAuthToken();
    
    if (!token) {
      return { success: false, message: 'No authentication token. Please login again.' };
    }

    const response = await fetch(`${API_URL}/recruitment/candidate/${candidateId}/feedback`, {
      method: 'GET',
      headers: authHeaders()
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Unknown error' }));
      return { success: false, message: error.message || 'Failed to fetch feedback' };
    }
    
    const data = await response.json();
    return { success: data.success, data: data.data };
  } catch (err) {
    console.error('💥 Network error in getCandidateFeedback:', err);
    return { success: false, message: 'Network error: ' + err.message };
  }
};

// Get All Feedback API
export const getAllFeedback = async () => {
  try {
    const token = cleanAuthToken();
    
    if (!token) {
      return { success: false, message: 'No authentication token. Please login again.' };
    }

    const response = await fetch(`${API_URL}/recruitment/feedback`, {
      method: 'GET',
      headers: authHeaders()
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Unknown error' }));
      return { success: false, message: error.message || 'Failed to fetch all feedback' };
    }
    
    const data = await response.json();
    return { success: data.success, data: data.data };
  } catch (err) {
    console.error('💥 Network error in getAllFeedback:', err);
    return { success: false, message: 'Network error: ' + err.message };
  }
};

// Create Feedback API
export const createFeedback = async ({ candidateId, comment, status }) => {
  try {
    const token = cleanAuthToken();
    
    if (!token) {
      return { success: false, message: 'No authentication token. Please login again.' };
    }

    const response = await fetch(`${API_URL}/recruitment/feedback`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ candidateId, comment, status })
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Unknown error' }));
      return { success: false, message: error.message || 'Failed to create feedback' };
    }
    
    const data = await response.json();
    return { success: data.success, data: data.data };
  } catch (err) {
    console.error('💥 Network error in createFeedback:', err);
    return { success: false, message: 'Network error: ' + err.message };
  }
};

// Get feedback by candidate ID
export const getFeedbackByCandidateId = async (candidateId) => {
  try {
    console.log('📥 API getFeedbackByCandidateId: Fetching feedback for candidate', candidateId);
    const response = await fetch(`${API_URL}/recruitment/candidate/${candidateId}/feedback`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' }
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Unknown error' }));
      return { success: false, message: error.message || 'Failed to fetch feedback' };
    }
    
    const data = await response.json();
    return { success: data.success, data: data.data };
  } catch (err) {
    console.error('💥 Network error in getFeedbackByCandidateId:', err);
    return { success: false, message: 'Network error: ' + err.message };
  }
};

// Update Feedback API
export const updateFeedback = async (feedbackId, { comment, status }) => {
  try {
    console.log('🔄 API updateFeedback: Updating feedback', feedbackId);
    const response = await fetch(`${API_URL}/recruitment/feedback/${feedbackId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ comment, status })
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Unknown error' }));
      return { success: false, message: error.message || 'Failed to update feedback' };
    }
    
    const data = await response.json();
    return { success: data.success, message: data.message };
  } catch (err) {
    console.error('💥 Network error in updateFeedback:', err);
    return { success: false, message: 'Network error: ' + err.message };
  }
};

// Delete Feedback API
export const deleteFeedback = async (feedbackId) => {
  try {
    console.log('🗑️ API deleteFeedback: Deleting feedback', feedbackId);
    const response = await fetch(`${API_URL}/recruitment/feedback/${feedbackId}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' }
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Unknown error' }));
      return { success: false, message: error.message || 'Failed to delete feedback' };
    }
    
    const data = await response.json();
    return { success: data.success, message: data.message };
  } catch (err) {
    console.error('💥 Network error in deleteFeedback:', err);
    return { success: false, message: 'Network error: ' + err.message };
  }
};

// ============================================================
// JOBS APIs
// ============================================================

// Get Jobs API
export const getJobs = async () => {
  try {
    console.log('🔍 API getJobs: Fetching jobs');
    const response = await fetch(`${API_URL}/recruitment/jobs`, {
      method: 'GET',
      headers: authHeaders()
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Unknown error' }));
      return { success: false, message: error.message || 'Failed to fetch jobs' };
    }
    
    const data = await response.json();
    return { success: data.success, data: data.data };
  } catch (err) {
    console.error('💥 Network error in getJobs:', err);
    return { success: false, message: 'Network error: ' + err.message };
  }
};

// Get Job by ID API
export const getJobById = async (jobId) => {
  try {
    console.log('🔍 API getJobById: Fetching job', jobId);
    const response = await fetch(`${API_URL}/recruitment/jobs/${jobId}`, {
      method: 'GET',
      headers: authHeaders()
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Unknown error' }));
      return { success: false, message: error.message || 'Failed to fetch job details' };
    }

    const data = await response.json();
    return { success: data.success, data: data.data };
  } catch (err) {
    console.error('💥 Network error in getJobById:', err);
    return { success: false, message: 'Network error: ' + err.message };
  }
};

// Create Job API
export const createJob = async (jobData) => {
  try {
    console.log('➕ API createJob: Creating job', jobData.title);
    const response = await fetch(`${API_URL}/recruitment/jobs`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(jobData)
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Unknown error' }));
      return { success: false, message: error.message || 'Failed to create job' };
    }
    
    const data = await response.json();
    return { success: data.success, message: data.message, data: data.data };
  } catch (err) {
    console.error('💥 Network error in createJob:', err);
    return { success: false, message: 'Network error: ' + err.message };
  }
};

// Update Job API
export const updateJob = async (jobId, jobData) => {
  try {
    console.log('✏️ API updateJob: Updating job', jobId);
    const response = await fetch(`${API_URL}/recruitment/jobs/${jobId}`, {
      method: 'PUT',
      headers: authHeaders(),
      body: JSON.stringify(jobData)
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Unknown error' }));
      return { success: false, message: error.message || 'Failed to update job' };
    }
    
    const data = await response.json();
    return { success: data.success, message: data.message };
  } catch (err) {
    console.error('💥 Network error in updateJob:', err);
    return { success: false, message: 'Network error: ' + err.message };
  }
};

// Delete Job API
export const deleteJob = async (jobId) => {
  try {
    console.log('🗑️ API deleteJob: Deleting job', jobId);
    const response = await fetch(`${API_URL}/recruitment/jobs/${jobId}`, {
      method: 'DELETE',
      headers: authHeaders()
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Unknown error' }));
      return { success: false, message: error.message || 'Failed to delete job' };
    }
    
    const data = await response.json();
    return { success: data.success, message: data.message };
  } catch (err) {
    console.error('💥 Network error in deleteJob:', err);
    return { success: false, message: 'Network error: ' + err.message };
  }
};

// Apply for Job API
export const applyForJob = async (candidateId, jobId, job_comment = '') => {
  try {
    console.log('📝 API applyForJob: Applying for job', jobId);
    const response = await fetch(`${API_URL}/recruitment/candidate/${candidateId}/apply-job`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ candidate_id: candidateId, job_id: jobId, job_comment })
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Unknown error' }));
      return { success: false, ...error };
    }
    
    const data = await response.json();
    return { success: data.success, data: data.data, message: data.message };
  } catch (err) {
    console.error('💥 Network error in applyForJob:', err);
    return { success: false, message: 'Network error: ' + err.message };
  }
};

// Get Candidate Job Applications API (returns latest entry per job)
export const getCandidateJobApplications = async (candidateId) => {
  try {
    console.log('🔍 API getCandidateJobApplications: Fetching applications for candidate', candidateId);
    const response = await fetch(`${API_URL}/recruitment/candidate/${candidateId}/job-applications`, {
      method: 'GET',
      headers: authHeaders()
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Unknown error' }));
      return { success: false, message: error.message || 'Failed to fetch applications' };
    }

    const data = await response.json();
    return { success: data.success, data: data.data };
  } catch (err) {
    console.error('💥 Network error in getCandidateJobApplications:', err);
    return { success: false, message: 'Network error: ' + err.message };
  }
};

// Get All Job Applications for a Job API (for job detail page)
export const getJobApplications = async (jobId, candidateId) => {
  try {
    console.log('🔍 API getJobApplications: Fetching job applications for job', jobId, 'candidate', candidateId);
    const query = candidateId ? `?candidate_id=${encodeURIComponent(candidateId)}` : '';
    const response = await fetch(`${API_URL}/recruitment/job/${jobId}/applications${query}`, {
      method: 'GET',
      headers: authHeaders()
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Unknown error' }));
      return { success: false, message: error.message || 'Failed to fetch applications' };
    }

    const data = await response.json();
    return { success: data.success, data: data.data };
  } catch (err) {
    console.error('💥 Network error in getJobApplications:', err);
    return { success: false, message: 'Network error: ' + err.message };
  }
};

export const notifyJobApplication = async (candidateId, jobId, payload) => {
  try {
    console.log('📧 API notifyJobApplication: Sending notification for job', jobId);
    const response = await fetch(`${API_URL}/recruitment/candidate/${candidateId}/apply-job/notify`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ job_id: jobId, ...payload })
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Unknown error' }));
      return { success: false, message: error.message || 'Failed to send notification email' };
    }

    const data = await response.json();
    return { success: data.success, message: data.message };
  } catch (err) {
    console.error('💥 Network error in notifyJobApplication:', err);
    return { success: false, message: 'Network error: ' + err.message };
  }
};

// Update Application Status API
export const updateApplicationStatus = async (mappingId, status, job_comment = '') => {
  try {
    console.log('✏️ API updateApplicationStatus: Updating status for mapping', mappingId);
    const response = await fetch(`${API_URL}/recruitment/job-application/${mappingId}/status`, {
      method: 'PUT',
      headers: authHeaders(),
      body: JSON.stringify({ status, job_comment })
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Unknown error' }));
      return { success: false, message: error.message || 'Failed to update status' };
    }
    
    const data = await response.json();
    return { success: data.success, message: data.message };
  } catch (err) {
    console.error('💥 Network error in updateApplicationStatus:', err);
    return { success: false, message: 'Network error: ' + err.message };
  }
};

// Withdraw Application API (creates new entry with withdrawn status)
export const withdrawApplication = async (candidateId, jobId, job_comment = 'Application withdrawn by candidate') => {
  try {
    console.log('🗑️ API withdrawApplication: Withdrawing application for candidate', candidateId, 'job', jobId);
    const response = await fetch(`${API_URL}/recruitment/candidate/${candidateId}/job-application/withdraw`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders()
      },
      body: JSON.stringify({
        candidate_id: candidateId,
        job_id: jobId,
        job_comment
      })
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Unknown error' }));
      return { success: false, message: error.message || 'Failed to withdraw application' };
    }

    const data = await response.json();
    return { success: data.success, message: data.message };
  } catch (err) {
    console.error('💥 Network error in withdrawApplication:', err);
    return { success: false, message: 'Network error: ' + err.message };
  }
};

// Update Job Application Status API (creates new entry)
export const updateJobApplicationStatus = async (candidateId, jobId, status, job_comment = '') => {
  try {
    console.log('✏️ API updateJobApplicationStatus: Updating status for candidate', candidateId, 'job', jobId);
    const response = await fetch(`${API_URL}/recruitment/candidate/${candidateId}/job-application/status`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ job_id: jobId, status, job_comment })
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Unknown error' }));
      return { success: false, message: error.message || 'Failed to update application status' };
    }

    const data = await response.json();
    return { success: data.success, message: data.message };
  } catch (err) {
    console.error('💥 Network error in updateJobApplicationStatus:', err);
    return { success: false, message: 'Network error: ' + err.message };
  }
};

// Add Job Comment API (creates a new comment entry in candidate_job_applications)
export const addJobComment = async (candidateId, jobId, job_comment = '', status = 'applied') => {
  try {
    console.log('📝 API addJobComment: Adding job comment for candidate', candidateId, 'job', jobId, 'status', status);
    const response = await fetch(`${API_URL}/recruitment/candidate/${candidateId}/job-application/comment`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ job_id: jobId, job_comment, status })
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Unknown error' }));
      return { success: false, message: error.message || 'Failed to add job comment' };
    }

    const data = await response.json();
    return { success: data.success, message: data.message, data: data.data };
  } catch (err) {
    console.error('💥 Network error in addJobComment:', err);
    return { success: false, message: 'Network error: ' + err.message };
  }
};

// Get Job Inquiry Details API (for admin)
export const getJobInquiry = async (jobId) => {
  try {
    console.log('🔍 API getJobInquiry: Fetching inquiry details for job', jobId);
    const response = await fetch(`${API_URL}/recruitment/jobs/${jobId}/inquiry`, {
      method: 'GET',
      headers: authHeaders()
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Unknown error' }));
      return { success: false, message: error.message || 'Failed to fetch job inquiry details' };
    }

    const data = await response.json();
    return { success: data.success, message: data.message, data: data.data };
  } catch (err) {
    console.error('💥 Network error in getJobInquiry:', err);
    return { success: false, message: 'Network error: ' + err.message };
  }
};

export const getJobApplicationSources = async (jobId) => {
  try {
    console.log('🔍 API getJobApplicationSources: Fetching application sources for job', jobId);
    const response = await fetch(`${API_URL}/recruitment/jobs/${jobId}/sources`, {
      method: 'GET',
      headers: authHeaders()
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Unknown error' }));
      return { success: false, message: error.message || 'Failed to fetch application sources' };
    }

    const data = await response.json();
    return { success: data.success, data: data.data };
  } catch (err) {
    console.error('💥 Network error in getJobApplicationSources:', err);
    return { success: false, message: 'Network error: ' + err.message };
  }
};

export const getAllJobApplicationSources = async () => {
  try {
    console.log('🔍 API getAllJobApplicationSources: Fetching all active job sources');
    const response = await fetch(`${API_URL}/recruitment/jobs/sources`, {
      method: 'GET',
      headers: authHeaders()
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Unknown error' }));
      return { success: false, message: error.message || 'Failed to fetch all application sources' };
    }

    const data = await response.json();
    return { success: data.success, data: data.data };
  } catch (err) {
    console.error('💥 Network error in getAllJobApplicationSources:', err);
    return { success: false, message: 'Network error: ' + err.message };
  }
};

export const addJobApplicationSource = async (jobId, sourceData) => {
  try {
    console.log('➕ API addJobApplicationSource: Adding application source for job', jobId);
    console.log('📤 API addJobApplicationSource payload:', sourceData);
    const endpoint = jobId ? `${API_URL}/recruitment/jobs/${jobId}/sources` : `${API_URL}/recruitment/jobs/sources`;
    console.log('📡 API addJobApplicationSource endpoint:', endpoint);
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        ...authHeaders(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(sourceData)
    });

    console.log('📥 API addJobApplicationSource status:', response.status);

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Unknown error' }));
      console.error('❌ API addJobApplicationSource error response:', error);
      return { success: false, message: error.message || 'Failed to add application source' };
    }

    const data = await response.json();
    console.log('✅ API addJobApplicationSource response:', data);
    return { success: data.success, data: data.data, message: data.message };
  } catch (err) {
    console.error('💥 Network error in addJobApplicationSource:', err);
    return { success: false, message: 'Network error: ' + err.message };
  }
};

export const updateJobApplicationSource = async (jobId, sourceId, sourceData) => {
  try {
    console.log('✏️ API updateJobApplicationSource: Updating source', sourceId, 'for job', jobId);
    const response = await fetch(`${API_URL}/recruitment/jobs/${jobId}/sources/${sourceId}`, {
      method: 'PUT',
      headers: {
        ...authHeaders(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(sourceData)
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Unknown error' }));
      return { success: false, message: error.message || 'Failed to update application source' };
    }

    const data = await response.json();
    return { success: data.success, data: data.data, message: data.message };
  } catch (err) {
    console.error('💥 Network error in updateJobApplicationSource:', err);
    return { success: false, message: 'Network error: ' + err.message };
  }
};

// Get Tech Stacks
export const getTechStacks = async () => {
  try {
    const response = await fetch(`${API_URL}/recruitment/tech-stacks`, {
      method: 'GET',
      headers: authHeaders()
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Unknown error' }));
      return { success: false, message: error.message || 'Failed to fetch tech stacks' };
    }
    const data = await response.json();
    return { success: true, data: data.data || data };
  } catch (err) {
    console.error('💥 Network error in getTechStacks:', err);
    return { success: false, message: 'Network error: ' + err.message };
  }
};

// Get Candidates by Tech ID (user_tech_map)
export const getCandidatesByTech = async (techId) => {
  try {
    const response = await fetch(`${API_URL}/recruitment/tech/${encodeURIComponent(techId)}/candidates`, {
      method: 'GET',
      headers: authHeaders()
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Unknown error' }));
      return { success: false, message: error.message || 'Failed to fetch candidates for tech' };
    }
    const data = await response.json();
    return { success: true, data: data.data || data.candidates || data };
  } catch (err) {
    console.error('💥 Network error in getCandidatesByTech:', err);
    return { success: false, message: 'Network error: ' + err.message };
  }
};

// Get email mapping(s) for a candidate (email_user_map + email_templates)
export const getCandidateEmails = async (candidateId) => {
  try {
    const response = await fetch(`${API_URL}/recruitment/candidate/${encodeURIComponent(candidateId)}/emails`, {
      method: 'GET',
      headers: authHeaders()
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Unknown error' }));
      return { success: false, message: error.message || 'Failed to fetch candidate emails' };
    }
    const data = await response.json();
    return { success: true, data: data.data || data.emails || data };
  } catch (err) {
    console.error('💥 Network error in getCandidateEmails:', err);
    return { success: false, message: 'Network error: ' + err.message };
  }
};

// Get candidate resume by email (for enriching recipients without candidateId)
export const getCandidateResumeByEmail = async (email) => {
  try {
    const response = await fetch(`${API_URL}/recruitment/candidate-resume/${encodeURIComponent(email)}`, {
      method: 'GET',
      headers: authHeaders()
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Unknown error' }));
      return { success: false, message: error.message || 'Candidate not found' };
    }
    const data = await response.json();
    return { success: true, data: data.data || data };
  } catch (err) {
    console.error('💥 Network error in getCandidateResumeByEmail:', err);
    return { success: false, message: 'Network error: ' + err.message };
  }
};

// Send bulk emails (frontend will call backend endpoint to actually send)
export const sendBulkEmails = async (payload) => {
  try {
    console.log('📬 API sendBulkEmails: Sending bulk emails');
    const response = await fetch(`${API_URL}/recruitment/send-emails`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify(payload)
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Unknown error' }));
      return { success: false, message: error.message || 'Failed to send emails' };
    }
    const data = await response.json();
    return { success: data.success, data: data.data || data };
  } catch (err) {
    console.error('💥 Network error in sendBulkEmails:', err);
    return { success: false, message: 'Network error: ' + err.message };
  }
};

// Get Email Templates
export const getEmailTemplates = async () => {
  try {
    const response = await fetch(`${API_URL}/recruitment/email-templates`, {
      method: 'GET',
      headers: authHeaders()
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Unknown error' }));
      return { success: false, message: error.message || 'Failed to fetch email templates' };
    }
    const data = await response.json();
    return { success: true, data: data.data || data };
  } catch (err) {
    console.error('💥 Network error in getEmailTemplates:', err);
    return { success: false, message: 'Network error: ' + err.message };
  }
};

export const enhanceResumeSection = async ({ section, sectionText, key, jobDescription }) => {
  try {
    const body = { section, sectionText };
    if (jobDescription) body.jobDescription = jobDescription;
    if (key) body.key = key;
    console.log('API: enhanceResumeSection -> sending', { section, textLength: sectionText?.length, hasKey: !!key });
    const response = await fetch(`${API_URL}/recruitment/resume/enhance-section`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });

    console.log('API: enhanceResumeSection -> response status', response.status);
    const parsed = await response.json().catch(() => null);
    console.log('API: enhanceResumeSection -> response body', parsed);
    if (!response.ok) {
      return { success: false, message: parsed?.message || `HTTP Error: ${response.status}` };
    }

    return { success: parsed?.success !== false, data: parsed?.data || parsed };
  } catch (err) {
    console.error('enhanceResumeSection API Error:', err);
    return { success: false, message: 'Network error: ' + (err?.message || String(err)) };
  }
};

export const extractAllSections = async ({ file, key, jobDescription }) => {
  try {
    const formData = new FormData();
    formData.append('resumeFile', file);
    if (key) formData.append('key', key);
    if (jobDescription) formData.append('jobDescription', jobDescription);
    console.log('API: extractAllSections -> sending', { fileName: file?.name, fileSize: file?.size, hasKey: !!key });
    const response = await fetch(`${API_URL}/recruitment/resume/extract-all`, {
      method: 'POST',
      body: formData,
    });

    console.log('API: extractAllSections -> response status', response.status);
    const parsed = await response.json().catch(() => null);
    console.log('API: extractAllSections -> response body', parsed);
    if (!response.ok) {
      return { success: false, message: parsed?.message || `HTTP Error: ${response.status}` };
    }

    return { success: parsed?.success !== false, data: parsed?.data || parsed };
  } catch (err) {
    console.error('extractAllSections API Error:', err);
    return { success: false, message: 'Network error: ' + (err?.message || String(err)) };
  }
};

export const createResumeVersion = async ({ candidateId } = {}) => {
  try {
    const response = await fetch(`${API_URL}/recruitment/resume/version`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ candidateId }),
    });

    const parsed = await response.json().catch(() => null);
    if (!response.ok) {
      return { success: false, message: parsed?.message || `HTTP Error: ${response.status}` };
    }

    return { success: true, data: parsed?.data || parsed };
  } catch (err) {
    console.error('createResumeVersion API Error:', err);
    return { success: false, message: 'Network error: ' + (err?.message || String(err)) };
  }
};

export const saveResumeData = async ({ candidateId, resumeDraft }) => {
  try {
    const response = await fetch(`${API_URL}/recruitment/resume/save`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ candidateId, resumeDraft }),
    });

    const parsed = await response.json().catch(() => null);
    if (!response.ok) {
      return { success: false, message: parsed?.message || `HTTP Error: ${response.status}` };
    }

    return { success: true, data: parsed?.data || parsed };
  } catch (err) {
    console.error('saveResumeData API Error:', err);
    return { success: false, message: 'Network error: ' + (err?.message || String(err)) };
  }
};

export const updateResumeData = async ({ candidateId, resumeDraft }) => {
  try {
    const response = await fetch(`${API_URL}/recruitment/resume/${candidateId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ resumeDraft }),
    });

    const parsed = await response.json().catch(() => null);
    if (!response.ok) {
      return { success: false, message: parsed?.message || `HTTP Error: ${response.status}` };
    }

    return { success: true, data: parsed?.data || parsed };
  } catch (err) {
    console.error('updateResumeData API Error:', err);
    return { success: false, message: 'Network error: ' + (err?.message || String(err)) };
  }
};

export const getResumeData = async (candidateId) => {
  try {
    const response = await fetch(`${API_URL}/recruitment/resume/${candidateId}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });

    const parsed = await response.json().catch(() => null);
    if (!response.ok) {
      return { success: false, message: parsed?.message || `HTTP Error: ${response.status}` };
    }

    return { success: true, data: parsed?.data || parsed };
  } catch (err) {
    console.error('getResumeData API Error:', err);
    return { success: false, message: 'Network error: ' + (err?.message || String(err)) };
  }
};

export const getResumeSectionData = async (resumeId, section) => {
  try {
    const safeResumeId = String(resumeId || '');
    const safeSection = encodeURIComponent(String(section || '').trim());
    const url = `${API_URL}/recruitment/resume/${encodeURIComponent(safeResumeId)}/${safeSection}`;

    console.log('UI: getResumeSectionData -> request', {
      resumeId: safeResumeId,
      section: section || '',
      url,
    });

    const response = await fetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });

    const parsed = await response.json().catch(() => null);
    console.log('UI: getResumeSectionData -> response', {
      status: response.status,
      ok: response.ok,
      resumeId: safeResumeId,
      section: section || '',
      parsed,
    });

    if (!response.ok) {
      return { success: false, message: parsed?.message || `HTTP Error: ${response.status}`, data: parsed?.data || parsed };
    }

    return { success: true, data: parsed?.data || parsed };
  } catch (err) {
    console.error('UI: getResumeSectionData API Error:', err);
    return { success: false, message: 'Network error: ' + (err?.message || String(err)) };
  }
};

// Enhance all sections (server-side batch enhancement)
export const enhanceAllSections = async ({ sections, jobDescription, key } = {}) => {
  try {
    const body = { sections: sections || {} };
    if (jobDescription) body.jobDescription = jobDescription;
    if (key) body.key = key;
    console.log('API: enhanceAllSections -> sending', { sectionsKeys: Object.keys(body.sections || {}), hasKey: !!key });
    const response = await fetch(`${API_URL}/recruitment/resume/enhance-all`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });

    const parsed = await response.json().catch(() => null);
    if (!response.ok) {
      return { success: false, message: parsed?.message || `HTTP Error: ${response.status}`, data: parsed?.data || parsed };
    }

    return { success: parsed?.success !== false, data: parsed?.data || parsed };
  } catch (err) {
    console.error('enhanceAllSections API Error:', err);
    return { success: false, message: 'Network error: ' + (err?.message || String(err)) };
  }
};

// Convenience wrappers for saving specific resume sections. These forward to saveResumeData
// with a minimal resumeDraft shaped for the backend.
const saveResumeSection = async ({ candidateId, resumeId, fieldName, value }) => {
  const resumeDraft = { [fieldName]: value };
  if (resumeId) resumeDraft.resumeId = Number(resumeId);

  const payload = {
    candidateId: candidateId ? Number(candidateId) : null,
    resumeDraft,
  };

  console.log('[API] saveResumeSection route decision:', {
    fieldName,
    candidateId,
    resumeId,
    usingUpdateRoute: Boolean(resumeId && candidateId),
    payloadPreview: payload,
  });

  if (resumeId && candidateId) {
    return updateResumeData({ candidateId: Number(candidateId), resumeDraft });
  }

  return saveResumeData({ candidateId: candidateId ? Number(candidateId) : null, resumeDraft });
};

export const saveProfessionalSummary = async ({ candidateId, resumeId, professionalSummary } = {}) => {
  return saveResumeSection({ candidateId, resumeId, fieldName: 'professionalSummary', value: professionalSummary || '' });
};

export const saveTechnicalSkills = async ({ candidateId, resumeId, technicalSkills } = {}) => {
  return saveResumeSection({ candidateId, resumeId, fieldName: 'technicalSkills', value: technicalSkills || [] });
};

export const saveWorkExperience = async ({ candidateId, resumeId, workExperience } = {}) => {
  return saveResumeSection({ candidateId, resumeId, fieldName: 'workExperience', value: workExperience || [] });
};

export const saveProjectExperience = async ({ candidateId, resumeId, projectExperience } = {}) => {
  return saveResumeSection({ candidateId, resumeId, fieldName: 'projectExperience', value: projectExperience || [] });
};

export const saveEducation = async ({ candidateId, resumeId, education } = {}) => {
  return saveResumeSection({ candidateId, resumeId, fieldName: 'education', value: education || [] });
};

export const saveLanguages = async ({ candidateId, resumeId, languages } = {}) => {
  return saveResumeSection({ candidateId, resumeId, fieldName: 'languages', value: languages || '' });
};

export const saveCertifications = async ({ candidateId, resumeId, certifications } = {}) => {
  return saveResumeSection({ candidateId, resumeId, fieldName: 'certifications', value: certifications || [] });
};

export const saveAchievements = async ({ candidateId, resumeId, achievements } = {}) => {
  return saveResumeSection({ candidateId, resumeId, fieldName: 'achievements', value: achievements || [] });
};